import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
import { Journey } from "./journey.js";
import { Deviation } from "./deviation.js";
import { Alert } from "./alert.js";
import { SourceHealth } from "./sources.js";
import { Junction } from "./junction.js";
import { AnticipationEvent } from "./anticipation.js";
/**
 * Transport temps réel : SSE. Un canal unique
 *   GET /api/v1/networks/{networkId}/stream?topics=journeys,deviations,alerts,sources
 * Flux entièrement descendant (l'abandon de la messagerie a supprimé tout besoin bidirectionnel).
 *
 * Chaque événement SSE :
 *   id:    = `seq` global monotone (permet la reprise via `Last-Event-ID`)
 *   event: = le `topic`
 *   data:  = StreamEnvelope (JSON)
 *
 * Séquence frontend (D-009) : charger l'instantané REST du sujet — qui porte un
 * champ `seq` —, puis appliquer les deltas de `seq` strictement supérieur. Un
 * `seq` de delta non consécutif ⇒ recharger l'instantané concerné.
 *
 * Forme du `payload` par sujet :
 *   journeys / deviations / alerts : { upserts: T[], removals: string[] }  (idempotent)
 *   sources                        : { sources: SourceHealth[] }           (remplacement complet)
 *   junctions                      : { upserts: Junction[], removals: string[] }
 */
export const StreamTopic = z.enum([
    "journeys",
    "deviations",
    "alerts",
    "sources",
    "junctions", // v1.1 — marqueur d'état de carrefour partagé entre postes (§12.5)
    "anticipation", // v1.44 — moteur d'anticipation (« Aide à la décision ») ; delta sans `plan`
]);
/**
 * Delta générique par collection (D-009) : les éléments à créer ou remplacer,
 * et les identifiants à retirer. Traitement **idempotent** côté client.
 */
const upsertRemove = (item) => z.object({ upserts: z.array(item), removals: z.array(OpaqueId) });
export const JourneysDelta = upsertRemove(Journey);
export const DeviationsDelta = upsertRemove(Deviation);
export const AlertsDelta = upsertRemove(Alert);
export const SourcesDelta = z.object({ sources: z.array(SourceHealth) });
export const JunctionsDelta = upsertRemove(Junction);
/** v1.44 — le delta SSE ne porte PAS `plan` (calcul lourd, REST seulement). */
export const AnticipationDelta = upsertRemove(AnticipationEvent.omit({ plan: true }));
export const StreamEnvelope = z.discriminatedUnion("topic", [
    z.object({
        topic: z.literal("journeys"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: JourneysDelta,
    }),
    z.object({
        topic: z.literal("deviations"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: DeviationsDelta,
    }),
    z.object({
        topic: z.literal("alerts"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: AlertsDelta,
    }),
    z.object({
        topic: z.literal("sources"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: SourcesDelta,
    }),
    z.object({
        topic: z.literal("junctions"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: JunctionsDelta,
    }),
    z.object({
        topic: z.literal("anticipation"),
        seq: z.number().int(),
        at: IsoDateTime,
        payload: AnticipationDelta,
    }),
]);
//# sourceMappingURL=stream.js.map